import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, ArrowRight, ShieldCheck, TrendingUp, Landmark } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand/Logo";

const PROOF = [
  { icon: ShieldCheck, text: "One membership for credit, building, reporting & funding" },
  { icon: TrendingUp, text: "See where you stand today — free, no card required" },
  { icon: Landmark, text: "Get funding-ready with a real, prioritized plan" },
];

const FIELDS = [
  ["name", "Full name", "text", "Jordan Avery", true],
  ["company_name", "Business name (optional)", "text", "Avery Ventures LLC", false],
  ["email", "Work email", "email", "you@company.com", true],
  ["password", "Password", "password", "••••••••", true],
];

export default function Register() {
  const { register, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", company_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      navigate("/home");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="font-inter flex min-h-screen bg-white" data-testid="register-page">
      {/* Left — slate brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[#0B1020] p-12 text-white lg:flex xl:p-16">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#5B4BE6]/25 via-transparent to-transparent" />
        <Link to="/" className="relative" data-testid="register-brand-logo"><Logo variant="light" height={30} /></Link>
        <div className="relative">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A5B4FC]"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Join the movement</div>
          <h2 className="font-head text-4xl font-extrabold leading-[1.1] tracking-tight xl:text-[44px]">Plan smarter. <span className="text-foil">Build bigger.</span></h2>
          <div className="mt-9 space-y-4">
            {PROOF.map((p) => (
              <div key={p.text} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5"><p.icon size={15} className="text-[#A5B4FC]" /></span>
                <span className="text-[14px] leading-snug text-slate-300">{p.text}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">People. Plan. Opportunity.</div>
      </div>

      {/* Right — form */}
      <div className="flex w-full flex-col items-center justify-center px-4 py-14 lg:w-1/2">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-block lg:hidden"><Logo variant="dark" height={34} /></Link>
          <h1 className="mt-7 font-head text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:mt-0">Create your account</h1>
          <p className="mt-2 text-[15px] text-slate-600">
            Already have an account? <Link to="/login" className="font-semibold text-[#5B4BE6] underline" data-testid="go-login-link">Sign in</Link>
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {FIELDS.map(([k, label, type, ph, required]) => (
              <div key={k}>
                <label className="mb-1.5 block text-[13px] font-medium text-slate-700">{label}</label>
                <input
                  type={type} value={form[k]} onChange={set(k)} required={required} placeholder={ph}
                  data-testid={`register-${k}-input`}
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-[15px] outline-none transition-colors focus:border-[#5B4BE6] focus:ring-2 focus:ring-[#5B4BE6]/15"
                />
              </div>
            ))}
            {error && <div data-testid="register-error" className="rounded-lg bg-red-50 px-3 py-2 text-[14px] text-red-600">{error}</div>}
            <button
              type="submit" disabled={loading} data-testid="register-submit-btn"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#5B4BE6] py-3.5 text-[16px] font-semibold text-white shadow-[0_16px_40px_-18px_rgba(91,75,230,0.8)] transition-all hover:-translate-y-0.5 hover:bg-[#4F46E5] disabled:opacity-60"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <>Create account <ArrowRight size={16} /></>}
            </button>
            <p className="text-center text-[12px] leading-relaxed text-slate-400">
              By creating an account you agree to our Terms & Privacy Policy. Joining is not an application for credit.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
